import { Check } from "@tamagui/lucide-icons-2";
import { useId, useState } from "react";
import { Text, View, debounce, Checkbox, Label, YStack, ScrollView } from "tamagui";
import { Checkboxes } from "./common/checkboxParts.mjs";
import { Platform } from "react-native-web";
import { jsx, jsxs } from "react/jsx-runtime";
const items = [{
  id: "ver-paypal",
  label: "PayPal",
  checked: false
}, {
  id: "ver-mastercard",
  label: "Mastercard",
  checked: false
}, {
  id: "ver-visa",
  label: "Visa",
  checked: false
}];
function VerticalCheckboxes() {
  const uniqueId = useId();
  const [values, setValues] = useState(() => items.reduce((a, b) => ({
    ...a,
    [b.id]: b.checked
  }), {}));
  const onValuesChange = debounce(values2 => {
    setValues(values2);
  });
  return /* @__PURE__ */jsxs(Checkboxes, {
    values,
    onValuesChange,
    width: 400,
    maxWidth: "100%",
    gap: "$4",
    children: [/* @__PURE__ */jsx(Checkboxes.Title, {
      children: "Title"
    }), /* @__PURE__ */jsx(Text, {
      fontSize: "$5",
      lineHeight: "$5",
      fontWeight: "300",
      col: "$gray10",
      children: "Your description goes in here"
    }), /* @__PURE__ */jsx(Checkboxes.FocusGroup, {
      gap: "$2",
      flexWrap: "wrap",
      children: items.map(({
        id,
        label,
        checked
      }) => /* @__PURE__ */jsx(Checkboxes.FocusGroup.Item, {
        value: id,
        children: /* @__PURE__ */jsx(Item, {
          uniqueId: id + uniqueId,
          id,
          label,
          checked
        })
      }))
    })]
  });
}
VerticalCheckboxes.fileName = "VerticalCheckboxes";
function Item({
  label,
  description,
  uniqueId,
  error,
  style
}) {
  const cardStyle = {};
  if (style) {
    Object.assign(cardStyle, style);
  }
  if (error) {
    Object.assign(cardStyle, {
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "rgb(255, 107, 107)"
    });
  }
  return /* @__PURE__ */jsx(View, {
    width: "100%",
    items: "center",
    children: /* @__PURE__ */jsxs(Checkboxes.Card, {
      flexDirection: "row",
      shrink: 1,
      items: "flex-start",
      gap: "$3",
      p: "$3",
      hoverStyle: {
        borderColor: "$color7"
      },
      cursor: "pointer",
      maxW: "100%",
      minW: "100%",
      style: cardStyle,
      children: [/* @__PURE__ */jsx(View, {
        flexDirection: "row",
        y: 1,
        children: /* @__PURE__ */jsx(Checkboxes.Checkbox, {
          id: uniqueId,
          children: /* @__PURE__ */jsx(Checkboxes.Checkbox.Indicator, {
            children: /* @__PURE__ */jsx(Check, {})
          })
        })
      }), /* @__PURE__ */jsxs(View, {
        flexDirection: "column",
        shrink: 1,
        children: [/* @__PURE__ */jsx(Checkboxes.Checkbox.Label, {
          cursor: "pointer",
          items: "flex-start",
          size: "$4",
          lineHeight: "$2",
          flexDirection: "column",
          htmlFor: uniqueId,
          children: label
        }), description ? /* @__PURE__ */jsx(Text, {
          shrink: 1,
          fontSize: "$3",
          lineHeight: "$3",
          fontWeight: "300",
          color: "$color9",
          children: description
        }) : null]
      })]
    })
  });
}
function VerticalCheckboxesControlled(props) {
  const uniqueId = useId();
  const [values, setValues] = useState(props.values);
  const onValuesChange = debounce(values2 => {
    setValues(values2);
    props.onChange({
      ...values2
    });
  });
  const checkboxList = props.items.map(({
    value,
    label
  }) => /* @__PURE__ */jsx(Checkboxes.FocusGroup.Item, {
    value,
    children: /* @__PURE__ */jsx(Item, {
      uniqueId: value + uniqueId,
      id: value,
      label: label || value,
      checked: (props.values || {})[value] === true,
      error: props.error,
      style: props.itemStyle
    })
  }, value));
  return /* @__PURE__ */jsxs(Checkboxes, {
    values,
    onValuesChange,
    maxW: "100%",
    "$group-window-sm": {
      px: "$4",
      py: "$6"
    },
    "$group-window-gtXs": {
      width: 400
    },
    gap: "$4",
    ...(props.containerProps || {}),
    children: [/* @__PURE__ */jsxs(YStack, {
      position: "sticky",
      top: 0,
      zIndex: 2,
      backgroundColor: "white",
      children: [props.title ? /* @__PURE__ */jsx(Checkboxes.Title, {
        children: props.title
      }) : null, props.label ? /* @__PURE__ */jsx(Text, {
        fontSize: "$5",
        lineHeight: "$5",
        fontWeight: "300",
        color: "$gray10",
        children: props.label
      }) : null]
    }), /* @__PURE__ */jsx(Checkboxes.FocusGroup, {
      minW: "100%",
      gap: "$2",
      flexWrap: "wrap",
      ...(props.itemContainerProps || {}),
      children: Platform.OS === "web" ? checkboxList : /* @__PURE__ */jsx(ScrollView, {
        backgroundColor: "white",
        children: checkboxList
      })
    })]
  });
}
const SingleCheckboxControlled = props => {
  const id = (props.id || "") + useId();
  const checkbox = /* @__PURE__ */jsx(Checkbox, {
    id,
    checked: props.checked,
    onCheckedChange: props.onCheckedChange,
    children: /* @__PURE__ */jsx(Checkbox.Indicator, {
      children: /* @__PURE__ */jsx(Check, {})
    })
  });
  return props.label ? /* @__PURE__ */jsxs(View, {
    flexDirection: "row",
    alignItems: "center",
    gap: "$3",
    children: [checkbox, /* @__PURE__ */jsx(Label, {
      size: "$3",
      htmlFor: id,
      children: props.label
    })]
  }) : checkbox;
};
const CustomSingleCheckboxControlled = props => {
  const id = (props.id || "") + useId();
  const bg = props.error ? "#ffc2c2ff" : props.checked ? "#85C19D" : "#E9ECEF";
  const checkbox = /* @__PURE__ */jsx(Checkbox, {
    id,
    checked: props.checked,
    bg,
    onCheckedChange: props.onCheckedChange,
    disabled: props.disabled,
    children: /* @__PURE__ */jsx(Checkbox.Indicator, {
      children: /* @__PURE__ */jsx(Check, {
        color: "#fff"
      })
    })
  });
  return props.label ? /* @__PURE__ */jsxs(View, {
    flexDirection: "row",
    alignItems: "center",
    gap: "$3",
    children: [checkbox, /* @__PURE__ */jsx(Label, {
      size: 14,
      style: {
        lineHeight: "14px"
      },
      htmlFor: id,
      color: props.disabled ? "#959595" : "#black",
      children: props.label
    })]
  }) : checkbox;
};
const CustomCircleSingleCheckboxControlled = props => {
  const id = (props.id || "") + useId();
  const checkbox = /* @__PURE__ */jsx(Checkbox, {
    unstyled: true,
    id,
    checked: props.checked,
    bg: "#E9ECEF",
    onCheckedChange: props.onCheckedChange,
    borderRadius: 100,
    borderColor: props.checked ? "#85C19D" : "transparent",
    borderWidth: props.checked ? 6 : 1,
    height: 20,
    width: 20,
    disabled: props.disabled,
    hoverStyle: {
      cursor: "pointer"
    }
  });
  return props.label ? /* @__PURE__ */jsxs(View, {
    flexDirection: "row",
    alignItems: "center",
    gap: "$3",
    height: 30,
    children: [checkbox, /* @__PURE__ */jsx(Label, {
      size: 14,
      htmlFor: id,
      color: props.disabled ? "#959595" : "#black",
      hoverStyle: {
        cursor: "pointer"
      },
      lineHeight: 15,
      children: props.label
    })]
  }) : checkbox;
};
export { CustomCircleSingleCheckboxControlled, CustomSingleCheckboxControlled, SingleCheckboxControlled, VerticalCheckboxes, VerticalCheckboxesControlled };
//# sourceMappingURL=VerticalCheckboxes.mjs.map
